import React, { useState, useEffect, useContext } from "react";
import { getMyReports, reopenReport } from "../api";
import { ToastContext } from "../App";

function formatCurrency(val) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(val || 0);
}

function formatDate(dateStr) {
  if (!dateStr) return "";
  // Slice to YYYY-MM-DD before parsing to prevent UTC-midnight ISO strings
  // from shifting the displayed date back one day in US timezones.
  const d = new Date(dateStr.slice(0, 10) + "T00:00:00");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

const STATUS_LABELS = {
  Submitted: { label: "Pending",  cls: "badge-submitted" },
  Approved:  { label: "Approved", cls: "badge-approved" },
  Rejected:  { label: "Rejected", cls: "badge-rejected" },
  Draft:     { label: "Draft",    cls: "badge-draft" },
};

const CATEGORY_ICONS = {
  Meals: "🍽️", Mileage: "🚗", Lodging: "🏨",
  Transportation: "🚌", Airfare: "✈️", "Office Supplies": "📦", Other: "🧾",
};

function ReportDetail({ report, onBack, onReopenReport }) {
  const showToast = useContext(ToastContext);
  const [reopening, setReopening] = useState(false);
  const lines = report.lines || [];
  const total = lines.reduce((s, l) => s + (Number(l.Amount) || 0), 0);
  const st    = STATUS_LABELS[report.ReportStatus] || STATUS_LABELS.Draft;

  const handleReopen = async () => {
    try {
      setReopening(true);
      await reopenReport(report.ID);
      showToast("Report reopened — make your changes and resubmit.", "success");
      onReopenReport(); // switches to the New Expense tab and reloads
    } catch (e) {
      showToast(e.message || "Failed to reopen report", "error");
      setReopening(false);
    }
  };

  return (
    <div>
      <button
        className="btn btn-ghost"
        style={{ width: "auto", marginBottom: 14, padding: "8px 14px", fontSize: 14 }}
        onClick={onBack}
      >
        ← Back
      </button>

      <div className="card" style={{ marginBottom: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div className="card-title">Expense Report</div>
            <div className="card-sub">Submitted {formatDate(report.SubmittedDate)}</div>
          </div>
          <span className={`badge ${st.cls}`}>{st.label}</span>
        </div>
        <div style={{ marginTop: 12, fontSize: 22, fontWeight: 700, color: "var(--blue)" }}>
          {formatCurrency(total)}
        </div>

        {report.ReportPurpose && (
          <div style={{ marginTop: 10, fontSize: 13, color: "var(--gray-5, #444)" }}>
            <strong>Purpose:</strong> {report.ReportPurpose}
          </div>
        )}

        {report.ReportStatus === "Rejected" && report.RejectionReason && (
          <div style={{
            marginTop: 10, background: "#fde7e9", borderRadius: 8,
            padding: "10px 12px", fontSize: 13, color: "var(--red)",
          }}>
            <strong>Returned:</strong> {report.RejectionReason}
          </div>
        )}

        {report.ReportStatus === "Approved" && (
          <div style={{
            marginTop: 10, background: "#dff6dd", borderRadius: 8,
            padding: "10px 12px", fontSize: 13, color: "var(--green)",
          }}>
            ✓ Approved by {report.ApproverName} on {formatDate(report.ApprovedDate)}
          </div>
        )}

        {report.ReportStatus === "Rejected" && (
          <button
            className="btn btn-primary"
            style={{ marginTop: 14 }}
            onClick={handleReopen}
            disabled={reopening}
          >
            {reopening ? "Reopening…" : "✏️ Edit & Resubmit"}
          </button>
        )}

        {report.ReportStatus === "Submitted" && (
          <div style={{ marginTop: 14 }}>
            <button
              className="btn btn-ghost"
              onClick={handleReopen}
              disabled={reopening}
              style={{ borderColor: "var(--orange, #e65100)", color: "var(--orange, #e65100)" }}
            >
              {reopening ? "Recalling…" : "↩ Recall & Edit Report"}
            </button>
            <div style={{ fontSize: 12, color: "var(--gray-4)", marginTop: 6 }}>
              This will pull the report back to draft so you can make changes before resubmitting.
              Only available while the report hasn't been approved yet.
            </div>
          </div>
        )}
      </div>

      {lines.map((line) => (
        <div key={line.ID} className="expense-card">
          <div className="expense-icon">{CATEGORY_ICONS[line.Category] || "🧾"}</div>
          <div className="expense-info">
            <div className="expense-category">{line.Category}</div>
            <div className="expense-desc">{line.Description || "—"}</div>
            <div className="expense-date">{formatDate(line.ExpenseDate)}</div>
            {line.ReceiptUrl && (
              <a
                href={line.ReceiptUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="expense-receipt"
              >
                📎 View receipt
              </a>
            )}
          </div>
          <div className="expense-right">
            <div className="expense-amount">{formatCurrency(line.Amount)}</div>
            {line.Category === "Mileage" && line.Miles && (
              <div style={{ fontSize: 11, color: "var(--gray-4)", marginTop: 2 }}>
                {line.Miles} mi
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function ReportHistory({ onReopenReport }) {
  const showToast = useContext(ToastContext);
  const [reports, setReports]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [selected, setSelected] = useState(null);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate]     = useState("");
  const [search, setSearch]     = useState("");

  useEffect(() => {
    getMyReports()
      .then(setReports)
      .catch(() => showToast("Failed to load reports", "error"))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  if (loading) return <div className="loader"><div className="spinner" /></div>;

  if (selected) {
    return (
      <ReportDetail
        report={selected}
        onBack={() => setSelected(null)}
        onReopenReport={onReopenReport}
      />
    );
  }

  if (reports.length === 0) {
    return (
      <div className="empty-state">
        <div className="empty-state-icon">📋</div>
        <div className="empty-state-title">No reports yet</div>
        <div className="empty-state-sub">Submitted reports will appear here.</div>
      </div>
    );
  }

  const filtered = reports.filter((r) => {
    const submitted = r.SubmittedDate ? r.SubmittedDate.slice(0, 10) : "";
    if (fromDate && submitted < fromDate) return false;
    if (toDate   && submitted > toDate)   return false;
    if (search) {
      const q = search.toLowerCase();
      const purpose = (r.ReportPurpose || "").toLowerCase();
      const status  = (r.ReportStatus  || "").toLowerCase();
      const lines   = (r.lines || []).map(l => (l.Description || "") + (l.Category || "")).join(" ").toLowerCase();
      if (!purpose.includes(q) && !status.includes(q) && !lines.includes(q)) return false;
    }
    return true;
  });

  const hasFilters = fromDate || toDate || search;

  return (
    <div>
      {/* Search + date filter bar */}
      <div style={{ marginBottom: 12 }}>
        <input
          type="text"
          className="form-control"
          placeholder="Search by purpose, category, description…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ marginBottom: 8 }}
        />
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input
            type="date"
            className="form-control"
            value={fromDate}
            onChange={e => setFromDate(e.target.value)}
            style={{ flex: 1 }}
            title="From date"
          />
          <span style={{ color: "var(--gray-4)", fontSize: 13 }}>–</span>
          <input
            type="date"
            className="form-control"
            value={toDate}
            onChange={e => setToDate(e.target.value)}
            style={{ flex: 1 }}
            title="To date"
          />
          {hasFilters && (
            <button
              className="btn btn-ghost"
              style={{ padding: "8px 12px", fontSize: 13, whiteSpace: "nowrap" }}
              onClick={() => { setFromDate(""); setToDate(""); setSearch(""); }}
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">🔍</div>
          <div className="empty-state-title">No reports found</div>
          <div className="empty-state-sub">Try adjusting the date range or search terms.</div>
        </div>
      ) : (
        <>
          <div className="divider">
            {hasFilters ? `${filtered.length} of ${reports.length} reports` : "Submitted Reports"}
          </div>
          {filtered.map((r) => {
        const st    = STATUS_LABELS[r.ReportStatus] || STATUS_LABELS.Draft;
        const total = (r.lines || []).reduce((s, l) => s + (Number(l.Amount) || 0), 0);
        return (
          <div key={r.ID} className="report-card" onClick={() => setSelected(r)}>
            <div className="report-card-header">
              <div>
                <span className={`badge ${st.cls}`}>{st.label}</span>
                <div className="report-card-date">
                  Submitted {formatDate(r.SubmittedDate)}
                </div>
                {r.ReportPurpose && (
                  <div style={{
                    fontSize: 12, color: "var(--gray-5, #444)", marginTop: 4,
                    maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}>
                    {r.ReportPurpose}
                  </div>
                )}
              </div>
              <div>
                <div className="report-card-total">{formatCurrency(total)}</div>
                <div className="report-card-lines">
                  {(r.lines || []).length} line{(r.lines || []).length !== 1 ? "s" : ""}
                </div>
              </div>
            </div>
          </div>
        );
          })}
        </>
      )}
    </div>
  );
}
